import { useEffect, useMemo, useState } from 'react';
import firebaseUtils from '../../utils/firebase-utils-simple';
import { updateCertificate, deleteCertificate } from '../../firebase/certificateUtils';
import { buildCertificatePdf, triggerDownload } from './PdfGenerator';
import '../../styles/components/certificates/CertificateManagement.css';

function CertificateManagement({ user }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    nombre: '',
    empresa: '',
    tipo: '',
    usuario: '',
    estado: ''
  });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: '',
    empresa: '',
    tipo: '',
    usuarioEmail: '',
    userId: '',
    estado: '',
    fecha: '',
    notaEntrega: '',
    normaAplicada: '',
    capacidadWLL: '',
    factorSeguridad: '',
    codigoUnico: '',
    numeroItem: ''
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const loadCertificates = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await firebaseUtils.getCertificados({ forceRefresh: false });
        setCertificates(data || []);
      } catch (err) {
        setError('No se pudieron cargar los certificados.');
      } finally {
        setLoading(false);
      }
    };

    loadCertificates();
  }, []);

  const filtered = useMemo(() => {
    return certificates.filter((cert) => {
      const matchNombre = filters.nombre ? (cert.nombre || '').toLowerCase().includes(filters.nombre.toLowerCase()) : true;
      const matchEmpresa = filters.empresa ? (cert.empresa || '').toLowerCase().includes(filters.empresa.toLowerCase()) : true;
      const matchTipo = filters.tipo ? (cert.tipo || '').toLowerCase().includes(filters.tipo.toLowerCase()) : true;
      const matchUsuario = filters.usuario ? ((cert.usuarioEmail || cert.userId || '').toLowerCase().includes(filters.usuario.toLowerCase())) : true;
      const matchEstado = filters.estado ? (cert.estado || '').toLowerCase().includes(filters.estado.toLowerCase()) : true;
      return matchNombre && matchEmpresa && matchTipo && matchUsuario && matchEstado;
    });
  }, [certificates, filters]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const startEdit = (cert) => {
    setEditing(cert.id);
    setActionError('');
    setForm({
      nombre: cert.nombre || '',
      empresa: cert.empresa || '',
      tipo: cert.tipo || '',
      usuarioEmail: cert.usuarioEmail || '',
      userId: cert.userId || '',
      estado: cert.estado || '',
      fecha: (cert.fecha || '').slice(0, 10),
      notaEntrega: cert.notaEntrega || '',
      normaAplicada: cert.normaAplicada || '',
      capacidadWLL: cert.capacidadWLL || cert.capacidad || '',
      factorSeguridad: cert.factorSeguridad || '',
      codigoUnico: cert.codigoUnico || '',
      numeroItem: cert.numeroItem || ''
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setActionError('');
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setActionError('');
      const payload = {
        ...form,
        fecha: form.fecha || new Date().toISOString()
      };
      await updateCertificate(editing, payload);
      setCertificates((prev) => prev.map((c) => (c.id === editing ? { ...c, ...payload } : c)));
      cancelEdit();
    } catch (err) {
      setActionError('No se pudo guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (id) => {
    try {
      setSaving(true);
      setActionError('');
      await deleteCertificate(id);
      // Refrescar la lista local sin recargar la página
      setCertificates((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setActionError('No se pudo eliminar el certificado.');
    } finally {
      setSaving(false);
    }
  };

  const regeneratePdf = (cert) => {
    try {
      const blob = buildCertificatePdf(cert);
      const filename = `${cert.codigoUnico || cert.nombre || 'certificado'}-${cert.id || Date.now()}.pdf`;
      triggerDownload(blob, filename);
    } catch (err) {
      setActionError('No se pudo generar el PDF local.');
    }
  };

  if (!user) {
    return <p>Debes iniciar sesión para administrar certificados.</p>;
  }

  if (user?.rol !== 'admin') {
    return <p>No tienes permisos para ver este módulo.</p>;
  }

  return (
    <div className="certificate-management">
      <header className="module-header">
        <h2>Gestión de certificados</h2>
        <p>Listado básico desde Firebase (con fallback si el caché falla).</p>
      </header>

      <div className="filters-grid">
        <label>Nombre
          <input name="nombre" value={filters.nombre} onChange={handleFilterChange} placeholder="Filtrar nombre" />
        </label>
        <label>Empresa
          <input name="empresa" value={filters.empresa} onChange={handleFilterChange} placeholder="Filtrar empresa" />
        </label>
        <label>Tipo
          <input name="tipo" value={filters.tipo} onChange={handleFilterChange} placeholder="Filtrar tipo" />
        </label>
        <label>Usuario/Email
          <input name="usuario" value={filters.usuario} onChange={handleFilterChange} placeholder="Filtrar usuario" />
        </label>
        <label>Estado
          <input name="estado" value={filters.estado} onChange={handleFilterChange} placeholder="Filtrar estado" />
        </label>
      </div>

      {loading && <p>Cargando certificados...</p>}
      {error && <p className="status-error">{error}</p>}

      {!loading && !error && certificates.length === 0 && (
        <p>No hay certificados registrados.</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="certificate-table-wrapper">
          <table className="certificate-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>Tipo</th>
                <th>Usuario</th>
                <th>Estado</th>
                <th>PDF</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cert) => (
                <tr key={cert.id} className={editing === cert.id ? 'editing' : ''}>
                  <td data-label="Nombre">{cert.nombre || 'Sin nombre'}</td>
                  <td data-label="Empresa">{cert.empresa || 'N/D'}</td>
                  <td data-label="Tipo">{cert.tipo || 'N/D'}</td>
                  <td data-label="Usuario">{cert.usuarioEmail || cert.userId || 'N/D'}</td>
                  <td data-label="Estado">{cert.estado || 'N/D'}</td>
                  <td data-label="PDF">
                    {cert.pdfUrl ? (
                      <a className="download-btn" href={cert.pdfUrl} target="_blank" rel="noreferrer">Descargar PDF</a>
                    ) : (
                      <button className="ghost-btn" type="button" onClick={() => regeneratePdf(cert)} disabled={saving}>
                        Generar PDF
                      </button>
                    )}
                  </td>
                  <td className="actions-cell" data-label="Acciones">
                    <button className="ghost-btn" type="button" onClick={() => startEdit(cert)} disabled={saving}>Editar</button>
                    <button className="ghost-btn danger" type="button" onClick={() => confirmDelete(cert.id)} disabled={saving}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="edit-panel">
          <div className="edit-header">
            <div>
              <p className="eyebrow">Editar certificado</p>
              <h3>{form.nombre || 'Sin nombre'}</h3>
            </div>
            <div className="edit-actions">
              <button type="button" className="ghost-btn" onClick={cancelEdit} disabled={saving}>Cancelar</button>
              <button type="button" className="primary-btn" onClick={submitEdit} disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
            </div>
          </div>

          <div className="edit-grid">
            <label>Nombre
              <input name="nombre" value={form.nombre} onChange={handleFormChange} />
            </label>
            <label>Empresa
              <input name="empresa" value={form.empresa} onChange={handleFormChange} />
            </label>
            <label>Tipo
              <input name="tipo" value={form.tipo} onChange={handleFormChange} />
            </label>
            <label>Usuario Email
              <input name="usuarioEmail" value={form.usuarioEmail} onChange={handleFormChange} />
            </label>
            <label>Estado
              <input name="estado" value={form.estado} onChange={handleFormChange} />
            </label>
            <label>Fecha
              <input type="date" name="fecha" value={form.fecha} onChange={handleFormChange} />
            </label>
            <label>Código Único
              <input name="codigoUnico" value={form.codigoUnico} onChange={handleFormChange} />
            </label>
            <label>Número Item
              <input name="numeroItem" value={form.numeroItem} onChange={handleFormChange} />
            </label>
            <label>Nota de entrega
              <input name="notaEntrega" value={form.notaEntrega} onChange={handleFormChange} />
            </label>
            <label>Norma aplicada
              <input name="normaAplicada" value={form.normaAplicada} onChange={handleFormChange} />
            </label>
            <label>Capacidad WLL
              <input name="capacidadWLL" value={form.capacidadWLL} onChange={handleFormChange} />
            </label>
            <label>Factor de Seguridad
              <input name="factorSeguridad" value={form.factorSeguridad} onChange={handleFormChange} />
            </label>
          </div>

          {actionError && <p className="status-error">{actionError}</p>}
        </div>
      )}
    </div>
  );
}

export default CertificateManagement;
